// src/layouts/SidebarLayout/components/SidebarMenu.tsx
import React, { useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Chip,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { SidebarLink } from '../../types';
import { useSidebar } from 'src/contexts/SidebarContext';


interface SidebarMenuProps {
  links: SidebarLink[];
  collapsed?: boolean;
  level?: number;
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => 
    !['isActive', 'isChild', 'collapsed'].includes(prop as string),
})<{
  isActive?: boolean;
  isChild?: boolean;
  collapsed?: boolean;
}>(({ theme, isActive, isChild, collapsed }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.25, 0), // Reduced from 0.5
  padding: collapsed ? theme.spacing(0.75, 1.5) : theme.spacing(0.75, 1.5), // Reduced padding
  minHeight: collapsed ? 40 : 36, // Reduced height
  position: 'relative',
  transition: 'all 0.2s ease-in-out',
  
  ...(isChild && {
    marginLeft: theme.spacing(1.5), // Reduced from 2
    paddingLeft: theme.spacing(2.5), // Reduced from 3
    minHeight: 32, // Even smaller for children
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 16, // Adjusted for smaller padding
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: 1,
    },
  }),

  ...(isActive && {
    background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important',
    color: '#FFFFFF',
    boxShadow: `0 6px 20px ${alpha('#4CAF50', 0.5)}, 0 0 30px ${alpha('#4CAF50', 0.3)}`,
    transform: 'translateX(6px) scale(1.03)',
    border: `2px solid ${alpha('#66BB6A', 0.6)}`,
    borderRadius: theme.spacing(1.5),

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 5,
      height: '70%',
      background: 'linear-gradient(180deg, #00897B 0%, #26A69A 100%)',
      borderRadius: '0 6px 6px 0',
      boxShadow: `0 0 15px ${alpha('#00897B', 0.6)}`,
      animation: 'neonGlow 2s ease-in-out infinite',
    },

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -2,
      borderRadius: theme.spacing(1.5),
      background: 'linear-gradient(135deg, #66BB6A, #00897B)',
      zIndex: -1,
      opacity: 0.4,
    },

    '& .MuiListItemIcon-root': {
      color: '#FFFFFF',
      filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
      transform: 'scale(1.1)',
    },
    '& .MuiListItemText-primary': {
      color: '#FFFFFF',
      fontWeight: 800,
      textShadow: `0 2px 4px ${alpha('#000000', 0.3)}`,
    },
    '& .sidebar-badge': {
      background: 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)',
      color: '#FFFFFF',
      fontWeight: 800,
      boxShadow: `0 0 10px ${alpha('#00897B', 0.5)}`,
    },
    '@keyframes neonGlow': {
      '0%, 100%': {
        opacity: 0.8,
        boxShadow: `0 0 15px ${alpha('#00897B', 0.6)}`,
      },
      '50%': {
        opacity: 1,
        boxShadow: `0 0 25px ${alpha('#00897B', 1)}`,
      },
    },
  }),

  ...(!isActive && {
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.15)} 0%, ${alpha('#00897B', 0.1)} 100%)`,
      transform: 'translateX(4px) scale(1.01)',
      borderLeft: `4px solid #4CAF50`,
      paddingLeft: theme.spacing(1.5),
      boxShadow: `0 4px 12px ${alpha('#4CAF50', 0.2)}`,
      borderRadius: theme.spacing(1.2),

      '& .MuiListItemIcon-root': {
        color: '#4CAF50',
        transform: 'scale(1.15) rotate(-5deg)',
        filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))',
      },
      '& .MuiListItemText-primary': {
        color: theme.palette.text.primary,
        fontWeight: 700,
      },
    },
  }),

  '& .MuiListItemIcon-root': {
    minWidth: collapsed ? 0 : 32, // Reduced from 40
    color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    transition: 'color 0.2s ease-in-out',
    justifyContent: 'center',
    marginRight: collapsed ? 0 : theme.spacing(1), // Add some spacing when not collapsed
  },

  '& .MuiListItemText-primary': {
    color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.85rem', // Slightly smaller font
    transition: 'color 0.2s ease-in-out',
  },
}));

const ExpandButton = styled(Box)<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: theme.spacing(0.75),
  background: isActive
    ? 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)'
    : `linear-gradient(135deg, ${alpha('#4CAF50', 0.15)} 0%, ${alpha('#00897B', 0.1)} 100%)`,
  border: isActive
    ? `1px solid ${alpha('#26A69A', 0.5)}`
    : `1px solid ${alpha('#4CAF50', 0.2)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  flexShrink: 0,
  boxShadow: isActive
    ? `0 0 10px ${alpha('#00897B', 0.4)}`
    : 'none',

  '&:hover': {
    transform: 'scale(1.1) rotate(180deg)',
    boxShadow: isActive
      ? `0 0 15px ${alpha('#00897B', 0.6)}`
      : `0 0 8px ${alpha('#4CAF50', 0.3)}`,
  },

  '& .MuiSvgIcon-root': {
    fontSize: 14,
    color: isActive
      ? '#FFFFFF'
      : '#4CAF50',
    fontWeight: 700,
  },
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.5, 0.75, 1.5),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(0.5),
  borderBottom: `2px solid ${alpha('#4CAF50', 0.2)}`,
  position: 'relative',
  '&:first-of-type': {
    marginTop: theme.spacing(0.5),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: theme.spacing(1.5),
    width: 60,
    height: 3,
    background: 'linear-gradient(90deg, #4CAF50 0%, #00897B 100%)',
    borderRadius: 2,
    boxShadow: `0 0 10px ${alpha('#4CAF50', 0.5)}`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    right: theme.spacing(1.5),
    width: 20,
    height: 3,
    background: 'linear-gradient(90deg, #00897B 0%, transparent 100%)',
    borderRadius: 2,
  },
}));

const SidebarMenuItem: React.FC<{
  link: SidebarLink;
  collapsed: boolean;
  level: number;
}> = ({ link, collapsed, level }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleCollapse, setCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  
  const isActive = location.pathname === link.route;
  const hasChildren = link.children && link.children.length > 0;
  const isChild = level > 0;
  
  // Check if any child is active
  const hasActiveChild = hasChildren && 
    link.children!.some(child => location.pathname === child.route);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    
    if (hasChildren) {
      if (collapsed) {
        // If collapsed and has children, expand sidebar and open this group
        setCollapsed(false);
        setTimeout(() => setIsOpen(true), 150); // Small delay for smooth animation
      } else {
        setIsOpen(!isOpen);
      }
    } else if (link.route) {
      navigate(link.route);
    }
  };

  const menuItem = (
    <StyledListItemButton
      onClick={handleClick}
      isActive={isActive}
      isChild={isChild}
      collapsed={collapsed}
      sx={{ cursor: 'pointer' }}
    >
      <ListItemIcon>
        {isChild ? (
          <FiberManualRecord sx={{ fontSize: 6 }} />
        ) : (
          link.icon
        )}
      </ListItemIcon>
      
      {!collapsed && (
        <>
          <ListItemText 
            primary={link.label}
            sx={{ flex: 1, minWidth: 0 }} // Allow text to shrink
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {link.badge && (
              <Chip
                label={link.badge}
                size="small"
                className="sidebar-badge"
                sx={{
                  height: 18, // Reduced from 20
                  fontSize: '0.65rem', // Smaller font
                  fontWeight: 600,
                  '& .MuiChip-label': {
                    px: 0.75, // Reduced padding
                  }
                }}
              />
            )}
            
            {hasChildren && (
              <ExpandButton isActive={isActive || hasActiveChild}>
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ExpandButton>
            )}
          </Box>
        </>
      )}
    </StyledListItemButton>
  );

  // For collapsed state, show tooltip with hierarchy info
  if (collapsed && !isChild) {
    const tooltipContent = hasChildren 
      ? `${link.label} (${link.children!.length} items)`
      : link.label;
    
    return (
      <Tooltip
        title={tooltipContent}
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              fontSize: '0.75rem',
              maxWidth: 200,
            },
          },
        }}
      >
        <Box>{menuItem}</Box>
      </Tooltip>
    );
  }

  return (
    <>
      {menuItem}
      {hasChildren && !collapsed && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 0.5 }}> {/* Reduced padding */}
            {link.children!.map((child) => (
              <SidebarMenuItem
                key={child.id}
                link={child}
                collapsed={false}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  links,
  collapsed = false,
  level = 0,
}) => {
  const visibleLinks = links.filter(link => link.visible !== false);

  if (visibleLinks.length === 0) {
    return null;
  }

  if (!collapsed && level === 0) {
    const groupedLinks = visibleLinks.reduce((acc, link) => {
      const category = link.category || 'uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(link);
      return acc;
    }, {} as Record<string, typeof visibleLinks>);

    return (
      <Box sx={{ width: '100%' }}>
        {Object.entries(groupedLinks).map(([category, categoryLinks]) => (
          <Box key={category}>
            {category !== 'dashboard' && category !== 'uncategorized' && (
              <CategoryHeader>
                <Typography
                  variant="caption"
                  sx={{
                    background: 'linear-gradient(90deg, #00897B 0%, #2196F3 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    filter: 'drop-shadow(0 1px 2px rgba(0, 137, 123, 0.3))',
                  }}
                >
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
              </CategoryHeader>
            )}
            <List disablePadding sx={{ px: 0.5 }}> 
              {categoryLinks.map((link) => (
                <SidebarMenuItem
                  key={link.id}
                  link={link}
                  collapsed={collapsed}
                  level={level}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <List disablePadding sx={{ width: '100%', px: collapsed ? 0.5 : 0 }}>
      {visibleLinks.map((link) => (
        <SidebarMenuItem
          key={link.id}
          link={link}
          collapsed={collapsed}
          level={level}
        />
      ))}
    </List>
  );
};

export default SidebarMenu;